import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Tooltip,
  Badge,
  message,
  Drawer,
  Table,
  Tag,
  Typography,
  Modal,
} from "antd";
import {
  PlusOutlined,
  WarningOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { supabase } from "../../../lib/supabase";
import { trackFeatureUsage, captureError } from "../../../lib/posthog";
import { TariffManagementTabProps, Tariff, TariffFilter } from "./utils/types";
import {
  getIncompleteTariffs,
  getSoonExpiringTariffs,
  getOverlappingTariffs,
  OverlappingTariff,
  getTariffSummary,
  formatTariffDateRange,
} from "./utils/tariffHelpers";
import TariffTable from "./tables/TariffTable";
import TariffForm from "./forms/TariffForm";
import TariffFilterForm, { TariffFilterValues } from "./forms/TariffFilterForm";

const { Text } = Typography;

const TariffManagementTab: React.FC<TariffManagementTabProps> = ({
  tariffs,
  transportTypes,
  originPorts,
  destinationPorts,
  tariffBrackets,
  containerSizes,
  bookingTypes,
  loading,
  fetchTariffs,
}) => {
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit" | "duplicate">(
    "create"
  );
  const [selectedTariff, setSelectedTariff] = useState<Tariff | undefined>(
    undefined
  );
  const [filterValues, setFilterValues] = useState<TariffFilterValues>({
    transportType: null,
    originPort: null,
    destinationPort: null,
    bookingType: null,
    containerSize: null,
    dateRange: [dayjs().startOf("day"), null],
    viewMode: "timeline",
    showActiveTariffs: true,
  });

  // State for overlapping tariffs drawer
  const [overlappingDrawerVisible, setOverlappingDrawerVisible] =
    useState(false);

  // Call fetchTariffs once on component mount with initial filter values
  useEffect(() => {
    fetchTariffs({
      showActiveTariffs: true,
      dateRange: [dayjs().startOf("day"), null],
    });
    trackFeatureUsage("tariff_management_view");
  }, []); // Empty dependency array to ensure it only runs once on mount

  // Handle editing a tariff
  const handleEdit = (tariff: Tariff) => {
    setSelectedTariff(tariff);
    setFormMode("edit");
    setFormVisible(true);
    trackFeatureUsage("tariff_edit_started");
  };

  // Handle duplicating a tariff
  const handleDuplicate = (tariff: Tariff) => {
    setSelectedTariff(tariff);
    setFormMode("duplicate");
    setFormVisible(true);
    trackFeatureUsage("tariff_duplicate_started");
  };

  // Handle deleting a tariff
  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase.from("tariffs").delete().eq("id", id);

      if (error) {
        throw error;
      }

      message.success("Tariff deleted successfully");
      fetchTariffs();
      trackFeatureUsage("tariff_deleted");
    } catch (error) {
      console.error("Error deleting tariff:", error);
      message.error("Failed to delete tariff");
      captureError(error, {
        source: "TariffManagementTab",
        action: "handleDelete",
      });
    }
  };

  // Handle form success (create, edit, duplicate)
  const handleFormSuccess = () => {
    setFormVisible(false);
    setSelectedTariff(undefined);
    fetchTariffs();
    message.success("Tariff saved successfully!");
  };

  // Handle form cancellation
  const handleFormCancel = () => {
    setFormVisible(false);
    setSelectedTariff(undefined);
  };

  // Create a new tariff
  const handleCreateTariff = () => {
    setSelectedTariff(undefined);
    setFormMode("create");
    setFormVisible(true);
    trackFeatureUsage("tariff_create_started");
  };

  // Handle filter changes
  const handleFilterChange = (values: Partial<TariffFilterValues>) => {
    // Update local filter values state
    setFilterValues((prev) => ({ ...prev, ...values }));

    // Create updated filter params based on the new values combined with existing ones
    const updatedValues = { ...filterValues, ...values };

    // Convert to the filter format expected by fetchTariffs
    const filterParams: TariffFilter = {};

    if (updatedValues.transportType !== null) {
      filterParams.transportType = updatedValues.transportType;
    }

    if (updatedValues.originPort !== null) {
      filterParams.originPort = updatedValues.originPort;
    }

    if (updatedValues.destinationPort !== null) {
      filterParams.destinationPort = updatedValues.destinationPort;
    }

    if (updatedValues.bookingType !== null) {
      filterParams.bookingType = updatedValues.bookingType;
    }

    if (updatedValues.containerSize !== null) {
      filterParams.containerSize = updatedValues.containerSize;
    }

    // Pass date range directly as dayjs objects
    if (updatedValues.dateRange && updatedValues.dateRange.length === 2) {
      filterParams.dateRange = updatedValues.dateRange;
    }

    // Include active tariffs flag
    filterParams.showActiveTariffs = updatedValues.showActiveTariffs;

    // Fetch tariffs with the updated filter parameters
    fetchTariffs(filterParams);

    trackFeatureUsage("tariff_filter_changed");
  };

  // Filter tariffs based on selected criteria
  const filteredTariffs = tariffs.filter((tariff) => {
    // Filter by transport type if selected
    if (
      filterValues.transportType &&
      tariff.transport_type !== filterValues.transportType
    ) {
      return false;
    }

    // Filter by origin port if selected
    if (
      filterValues.originPort &&
      tariff.origin_port !== filterValues.originPort
    ) {
      return false;
    }

    // Filter by destination port if selected
    if (
      filterValues.destinationPort &&
      tariff.destination_port !== filterValues.destinationPort
    ) {
      return false;
    }

    // Filter by booking type if selected
    if (
      filterValues.bookingType &&
      tariff.bookings_type !== filterValues.bookingType
    ) {
      return false;
    }

    // Filter by container size if selected
    if (
      filterValues.containerSize &&
      tariff.container_size !== filterValues.containerSize
    ) {
      return false;
    }

    // Include tariffs that overlap with the selected date range
    const tariffStart = tariff.from ? dayjs(tariff.from) : null;
    const tariffEnd = tariff.to ? dayjs(tariff.to) : null;
    const [startDate, endDate] = filterValues.dateRange;

    // If showing active tariffs (default)
    if (filterValues.showActiveTariffs) {
      const today = startDate; // startDate is already today's date from the dateRange

      // A tariff is active if:
      // 1. Has a from date before or equal to today AND
      // 2. Either has no end date or end date is after or equal to today

      // For tariffs with both from and to dates
      if (tariffStart && tariffEnd) {
        return (
          (tariffStart.isBefore(today) || tariffStart.isSame(today)) &&
          (tariffEnd.isAfter(today) || tariffEnd.isSame(today))
        );
      }

      // For tariffs with only from date (no end date)
      if (tariffStart && !tariffEnd) {
        return tariffStart.isBefore(today) || tariffStart.isSame(today);
      }

      // For tariffs with only end date (no from date)
      if (!tariffStart && tariffEnd) {
        return tariffEnd.isAfter(today) || tariffEnd.isSame(today);
      }

      // If neither from nor to date is specified, include the tariff
      return true;
    }

    // If date range is specified with both start and end
    if (startDate && endDate) {
      // If only start date is specified for tariff
      if (tariffStart && !tariffEnd) {
        return tariffStart.isBefore(endDate);
      }

      // If only end date is specified for tariff
      if (!tariffStart && tariffEnd) {
        return tariffEnd.isAfter(startDate);
      }

      // If both dates are specified for tariff, check for overlap
      if (tariffStart && tariffEnd) {
        return (
          ((tariffStart.isAfter(startDate) || tariffStart.isSame(startDate)) &&
            tariffStart.isBefore(endDate)) ||
          (tariffEnd.isAfter(startDate) &&
            (tariffEnd.isBefore(endDate) || tariffEnd.isSame(endDate))) ||
          (tariffStart.isBefore(startDate) && tariffEnd.isAfter(endDate))
        );
      }
    }

    return true;
  });

  // Get counts for badges
  const incompleteTariffs = getIncompleteTariffs(tariffs);
  const expiringTariffs = getSoonExpiringTariffs(tariffs);
  const overlappingTariffs = getOverlappingTariffs(tariffs);

  // Handle viewing overlapping tariffs
  const handleViewOverlappingTariffs = () => {
    setOverlappingDrawerVisible(true);
    trackFeatureUsage("overlapping_tariffs_viewed");
  };

  // Get severity color for overlap
  const getSeverityColor = (severity: "low" | "medium" | "high"): string => {
    switch (severity) {
      case "high":
        return "#f5222d";
      case "medium":
        return "#fa8c16";
      case "low":
        return "#52c41a";
      default:
        return "#fa8c16";
    }
  };

  // Columns for overlapping tariffs table
  const overlappingColumns = [
    {
      title: "Tariff 1",
      dataIndex: "tariff1",
      key: "tariff1",
      render: (tariff: Tariff) => (
        <div>
          <div>{getTariffSummary(tariff)}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatTariffDateRange(tariff)}
          </Text>
        </div>
      ),
    },
    {
      title: "Tariff 2",
      dataIndex: "tariff2",
      key: "tariff2",
      render: (tariff: Tariff) => (
        <div>
          <div>{getTariffSummary(tariff)}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatTariffDateRange(tariff)}
          </Text>
        </div>
      ),
    },
    {
      title: "Overlap Period",
      dataIndex: "overlapDays",
      key: "overlapDays",
      render: (_: unknown, record: OverlappingTariff) => (
        <div>
          <div>{`${record.overlapStart} to ${record.overlapEnd}`}</div>
          <Text type="secondary">{`${record.overlapDays} days`}</Text>
        </div>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (severity: "low" | "medium" | "high") => (
        <Tag
          color={getSeverityColor(severity)}
          style={{ textTransform: "capitalize" }}
        >
          {severity}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: OverlappingTariff) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record.tariff1)}>
            Edit Tariff 1
          </Button>
          <Button size="small" onClick={() => handleEdit(record.tariff2)}>
            Edit Tariff 2
          </Button>
        </Space>
      ),
    },
  ];

  // Render tariff form in a modal
  const renderTariffModal = () => {
    return (
      <Modal
        title={
          formMode === "create"
            ? "Create New Tariff"
            : formMode === "edit"
            ? "Edit Tariff"
            : "Duplicate Tariff"
        }
        open={formVisible}
        onCancel={handleFormCancel}
        footer={null}
        width={800}
        destroyOnClose
        maskClosable={true}
        style={{ top: 20 }}
      >
        <TariffForm
          key={`${formMode}-${selectedTariff?.id || "new"}`}
          mode={formMode}
          tariff={selectedTariff}
          transportTypes={transportTypes}
          originPorts={originPorts}
          destinationPorts={destinationPorts}
          tariffBrackets={tariffBrackets}
          containerSizes={containerSizes}
          bookingTypes={bookingTypes}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Modal>
    );
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateTariff}
          >
            Add Tariff
          </Button>
        </Space>

        <Space>
          {incompleteTariffs.length > 0 && (
            <Tooltip title="There are tariffs missing crucial information">
              <Badge
                count={incompleteTariffs.length}
                overflowCount={99}
                style={{ backgroundColor: "#ff4d4f" }}
              >
                <Button icon={<WarningOutlined />}>Incomplete Tariffs</Button>
              </Badge>
            </Tooltip>
          )}

          {expiringTariffs.length > 0 && (
            <Tooltip title="Tariffs expiring within 3 days">
              <Badge
                count={expiringTariffs.length}
                offset={[10, 0]}
                size="small"
              >
                <Button icon={<WarningOutlined />}>Expiring Soon</Button>
              </Badge>
            </Tooltip>
          )}

          {overlappingTariffs.length > 0 && (
            <Tooltip title="Tariffs with overlapping date ranges">
              <Badge
                count={overlappingTariffs.length}
                offset={[10, 0]}
                size="small"
                style={{ backgroundColor: "#fa8c16" }}
              >
                <Button
                  icon={<AlertOutlined />}
                  onClick={handleViewOverlappingTariffs}
                >
                  Overlapping Tariffs
                </Button>
              </Badge>
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Basic filter form - can be expanded as needed */}
      <TariffFilterForm
        transportTypes={transportTypes}
        originPorts={originPorts}
        destinationPorts={destinationPorts}
        containerSizes={containerSizes}
        bookingTypes={bookingTypes}
        values={filterValues}
        onChange={handleFilterChange}
      />

      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <Button
          type="primary"
          onClick={() => {
            // Reset filter values to defaults
            const today = dayjs().startOf("day");
            const defaultFilters: TariffFilterValues = {
              transportType: null,
              originPort: null,
              destinationPort: null,
              bookingType: null,
              containerSize: null,
              dateRange: [today, null] as [dayjs.Dayjs, null],
              viewMode: "timeline",
              showActiveTariffs: true,
            };

            // Update local state
            setFilterValues(defaultFilters);

            // Reset server-side filters - pass dayjs objects directly
            fetchTariffs({
              showActiveTariffs: true,
              dateRange: [today, null],
            });

            trackFeatureUsage("tariff_filters_reset");
          }}
        >
          Reset Filters
        </Button>
      </div>

      {/* Replace the conditional rendering with the modal */}
      {renderTariffModal()}

      <TariffTable
        tariffs={filteredTariffs}
        transportTypes={transportTypes}
        bookingTypes={bookingTypes}
        containerSizes={containerSizes}
        loading={loading}
        pageSize={20}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
      />

      {/* Drawer for displaying overlapping tariffs */}
      <Drawer
        title="Overlapping Tariffs"
        width={1000}
        open={overlappingDrawerVisible}
        onClose={() => setOverlappingDrawerVisible(false)}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            These tariffs have the same route, transport type, container size,
            booking type, and tariff bracket settings but their date ranges
            overlap. This could lead to confusion when determining which tariff
            should apply.
          </Text>
        </div>
        <Table
          dataSource={overlappingTariffs}
          columns={overlappingColumns}
          rowKey={(record) => `${record.tariff1.id}-${record.tariff2.id}`}
          pagination={{ pageSize: 10 }}
        />
      </Drawer>
    </div>
  );
};

export default TariffManagementTab;
